import type { InternalAxiosRequestConfig } from 'axios';

import { isAuthSessionExpiredError } from '@shared/errors/auth-session-expired-error';
import { acquireAccessToken } from '@shared/helpers/acquire-access-token/acquireAccessToken';
import { handleSessionExpired } from '@shared/helpers/session-expiry/handleSessionExpired';

/**
 * Request interceptor - adds authentication headers to all requests
 *
 * Headers added:
 * - Authorization: Bearer token (acquired from MSAL)
 * - Ocp-Apim-Subscription-Key: Azure API Management key
 * - X-App-Version: Frontend application build number
 *
 * Auth Session Handling:
 * - If token acquisition fails due to expired session, shows the inline
 *   session-expired screen at the current URL
 *
 * @param config - Axios request configuration
 * @returns Modified request configuration with auth headers
 */
export async function requestInterceptor(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  try {
    const token = await acquireAccessToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    if (isAuthSessionExpiredError(error)) {
      void handleSessionExpired();
      throw error;
    }

    // For other errors, continue without token - let backend handle 401
  }

  // Add Azure API Management subscription key if available
  const subscriptionKey = import.meta.env.VITE_SUBSCRIPTION_KEY;
  if (subscriptionKey) {
    config.headers['Ocp-Apim-Subscription-Key'] = subscriptionKey;
  }

  // Add frontend app version for App Insights tracing and debugging
  const appVersion = import.meta.env.VITE_BUILD_NUMBER;
  if (appVersion) {
    config.headers['X-App-Version'] = appVersion;
  }

  return config;
}
